import React, { useState, useEffect } from 'react';
import './Pricing.css';
import { Check, Cloud, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, handleRedirectResult } from '../utils/authUtils';
import { useNavigate, useLocation } from 'react-router-dom';

const Pricing = () => {
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
    const [isOpen, setIsOpen] = useState(false);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, authInitialized } = useAuth();

    // Deep link: /pricing#pricing-pro → scroll to $9.99/mo Pro card
    useEffect(() => {
        if (location.hash !== '#pricing-pro') return;
        const timer = window.setTimeout(() => {
            document.getElementById('pricing-pro')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }, 150);
        return () => window.clearTimeout(timer);
    }, [location.hash, location.pathname]);

    // Handle redirect result on page load
    useEffect(() => {
        const handleAuthRedirect = async () => {
            if (authInitialized) {
                const redirectResult = await handleRedirectResult();
                if (redirectResult.success && redirectResult.result) {
                    console.log('Redirect authentication successful');
                    navigate(`/main/${redirectResult.result.user.uid}`);
                }
            }
        };

        handleAuthRedirect();
    }, [authInitialized, navigate]);

    // Don't auto-navigate existing users - let them stay on pricing page

    const handleSignInWithGoogle = async () => {
        setAuthLoading(true);
        setAuthError(null);
        
        try {
            const result = await signInWithGoogle();
            
            if (result.method === 'popup' && result.result) {
                // For popup method, navigate immediately
                navigate(`/main/${result.result.user.uid}`);
                setShowSignUpModal(false);
            }
            // For redirect method, navigation will happen automatically
        } catch (error) {
            console.error('Authentication error:', error);
            setAuthError(error.message);
        } finally {
            setAuthLoading(false);
        }
    };
    const toggleModal = () => {
        setIsOpen(!isOpen);
    }  

    const plans = [
        {
            name: 'Free',
            monthlyPrice: 0,
            yearlyPrice: 0,
            features: [
                { text: '100 AI responses/month', included: true },
                { text: '3 document uploads/day', included: true },
                { text: 'Basic flashcards', included: true },
                { text: 'Community support', included: true },
                { text: 'Knowledge base integrations', included: true, icon: <Cloud className="feature-icon" /> }
            ],
            cta: 'Get Started Free',
            popular: false
        },
        {
            name: 'Pro',
            monthlyPrice: 9.99,
            yearlyPrice: 99,
            features: [
                { text: 'Unlimited AI responses/month', included: true },
                { text: '6/uploads/day', included: true },
                { text: 'Live quizzes', included: true },
                { text: 'Auto-grading', included: true },
                // { text: 'Sell courses', included: true },
                { text: 'Advanced analytics', included: true },
                { text: 'Export features', included: true, icon: <Cloud className="feature-icon" /> },
                { text: 'Email support', included: true, icon: <Info className="feature-icon" /> }
            ],
            cta: 'Get Pro',
            popular: true
        },
        // {
        //     name: 'Enterprise',
        //     subtitle: 'Everything in Pro +',
        //     monthlyPrice: 25,
        //     yearlyPrice: 250,
        //     features: [
        //         { text: 'Access to the real-time, proactive AI tutor', included: true, icon: <Info className="feature-icon" /> },
        //         { text: 'Unlimited responses/month', included: true },
        //         { text: 'Learning paths', included: true },
        //         { text: 'Advanced analytics', included: true },
        //         { text: 'Priority processing', included: true },
        //     ],
        //     cta: 'Get Enterprise',
        //     popular: false
        // }
    ];

    return (
        <div>
            {/* Navigation Header */}
            <header className="header">
                <div className="header-container">
                    <a href="/" className="logo">
                        <div className="logo-icon"><img className='logo-icon-img' src="https://iili.io/KYpg7rg.png" alt="StudySpace" /></div>
                        StudySpace
                    </a>
                    <nav className="nav">
                        <a href="/#features" className="nav-link">Features</a>
                        <a href="/pricing" className="nav-link">Pricing</a>
                        <a href="/mission" className="nav-link">Our Mission</a>
                    </nav>
                    <button onClick={toggleModal} className="sign-in-btn">Sign in</button>
                </div>
            </header>

            <div className="pricing-page-container">
                <div className="pricing-page-header">
                    <h1 className="pricing-page-title">Pricing Plans</h1>
                </div>

            <div className="pricing-billing-toggle">
                <button 
                    className={`pricing-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                    onClick={() => setBillingCycle('monthly')}
                >
                    Monthly
                </button>
                <button 
                    className={`pricing-toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                    onClick={() => setBillingCycle('yearly')}
                >
                    Yearly
                    <span className="pricing-save-badge">Save 17%</span>
                </button>
            </div>

            <div className="pricing-page-cards">
                {plans.map((plan, index) => {
                    const currentPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
                    
                    return (
                        <div
                            key={index}
                            id={plan.name === 'Pro' ? 'pricing-pro' : undefined}
                            className={`pricing-page-card ${plan.popular ? 'popular' : ''}`}
                        >
                            {plan.popular && (
                                <div className="pricing-popular-badge">Most Popular</div>
                            )}
                            
                            <div className="pricing-card-header">
                                <h2 className="pricing-plan-name">{plan.name}</h2>
                                {plan.subtitle && <p className="pricing-plan-subtitle">{plan.subtitle}</p>}
                            </div>

                            <div className="pricing-card-pricing">
                                <div className="pricing-price">
                                    ${currentPrice.toFixed(2)} USD/{billingCycle === 'monthly' ? 'month' : 'year'}
                                </div>
                                {plan.name !== 'Free' && (
                                    <p className="pricing-student-discount">
                                        Students with their education email get an extra 20% off all plans!
                                    </p>
                                )}
                            </div>

                            <div className="pricing-card-features">
                                {plan.features.map((feature, featureIndex) => (
                                    <div key={featureIndex} className="pricing-feature-item">
                                        <Check className="pricing-check-icon" />
                                        <span className="pricing-feature-text">
                                            {feature.text}
                                        </span>
                                        {feature.icon && feature.icon}
                                    </div>
                                ))}
                            </div>

                            <button onClick={toggleModal}  className={`pricing-cta-button ${plan.name === 'Enterprise' ? 'enterprise' : 'primary'}`}>
                                {plan.cta}
                            </button>
                        </div>
                    );
                })}
            </div>
            </div>

            {isOpen && (
        <div className="modal">
            <div className="modal-content">
                <button className="modal-close" onClick={toggleModal}>×</button>
                
                <h2 className="modal-title">Welcome to StudySpace</h2>
                
                        <button onClick={handleSignInWithGoogle} className="google-btn" disabled={authLoading}>
                    <div className="google-logo">G</div>
                    {authLoading ? 'Signing in...' : 'Continue with Google'}
                </button>
                
                {authError && (
                    <div className="auth-error">
                        {authError}
                    </div>
                )}
                
                <div className="separator">
                    <span>or</span>
                </div>
{/*                 
                <input type="email" className="email-input" placeholder="EMAIL" />
                
                <button className="email-btn">Continue with email</button> */}
                
                <p className="login-link">
                    Have an Account? <button style={{cursor: 'pointer', border: 'none', background: 'none', color: '#3b82f6', fontWeight: '500'}} onClick={handleSignInWithGoogle} disabled={authLoading}>Log In</button>
                </p>
            </div>
        </div>
    )}

            {/* Footer Section */}
            <footer className="footer">
                <div className="footer-container">
                    <div className="footer-left">
                        <div className="footer-logo">
                            <div className="footer-logo-icon">S</div>
                            <span className="footer-brand">StudySpace</span>
                        </div>
                        <p className="footer-copyright">© 2025 StudySpace, Boston, MA</p>
                        <div className="footer-status">
                            <Check className="status-icon" />
                            <span>All services are online.</span>
                        </div>
                    </div>
                    
                    <div className="footer-right">
                        <div className="footer-columns">
                            <div className="footer-column">
                                <a href="/#features" className="footer-link">Features</a>
                                <a href="/pricing" className="footer-link">Pricing</a>
                                <a href="/mission" className="footer-link">Our Mission</a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Pricing;